// src/ClientProfilePage.js
import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
	User,
	FileText,
	LogOut,
	Menu,
	X,
	MessageSquare,
	ChevronLeft,
	BarChart,
	Mic,
	Play
} from 'lucide-react';

// Import components from dashboard folder to follow therapist dashboard convention
import ClientProfile from './dashboard/ClientProfile';
import ClientResponses from './dashboard/ClientResponses';
import ClientPersonality from './dashboard/ClientPersonality';

const ClientProfilePage = () => {
	const [profile, setProfile] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [user, setUser] = useState(null);
	const [menuOpen, setMenuOpen] = useState(false);
	
	const navigate = useNavigate();
	const location = useLocation();

	// Get user from local storage
	useEffect(() => {
		const storedUser = localStorage.getItem('user');
		if (storedUser) {
			setUser(JSON.parse(storedUser));
		} else {
			// No user found, redirect to login
			navigate('/login');
		}
	}, [navigate]);

	// Fetch client profile
	useEffect(() => {
		if (user) {
			fetchClientProfile();
		}
	}, [user]);

	const fetchClientProfile = async () => {
		if (!user || !user.id) return;
		
		try {
			setLoading(true);
			const response = await fetch(`/api/client-profile/${user.id}`);
			
			if (!response.ok) {
				if (response.status === 404) {
					// Profile not found - not an error, just needs to be created
					setProfile(null);
					setError(null);
					setLoading(false);
					return;
				}
				throw new Error('Failed to fetch profile');
			}
			
			const data = await response.json();
			setProfile(data.profile);
			setError(null);
		} catch (error) {
			console.error('Error fetching client profile:', error);
			setError('Failed to load your profile. Please try again later.');
		} finally {
			setLoading(false);
		}
	};

	// Handle logout
	const handleLogout = async () => {
		try {
			await fetch('/api/auth/logout', {
				method: 'POST',
				credentials: 'include',
			});
			
			// Clear localStorage
			localStorage.removeItem('user');
			localStorage.removeItem('authToken');
			
			// Redirect to login
			navigate('/login');
		} catch (error) {
			console.error('Error logging out:', error);
		}
	};

	// Navigate to Natural Matchmaker
	const handleStartSession = () => {
		navigate('/natural-matchmaker');
	};

	// Get current section from path
	const getCurrentSection = () => {
		const path = location.pathname;
		if (path.includes('/responses')) return 'responses';
		if (path.includes('/personality')) return 'personality';
		return 'profile';
	};
	
	// Set page title based on section
	const getPageTitle = () => {
		const section = getCurrentSection();
		switch (section) {
			case 'responses':
				return 'Your Session Responses';
			case 'personality':
				return 'Your Personality Profile';
			default:
				return 'Your Dashboard';
		}
	};

	if (loading && !profile) {
		return (
			<div className="min-h-screen bg-blue-50 flex items-center justify-center">
				<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-blue-50 flex flex-col">
			{/* Header */}
			<header className="bg-blue-600 text-white p-4 shadow-md">
				<div className="container mx-auto flex items-center justify-between">
					<div className="flex items-center">
						<button 
							onClick={() => setMenuOpen(!menuOpen)}
							className="mr-4 p-1 hover:bg-blue-700 rounded-full block lg:hidden"
						>
							{menuOpen ? <X size={24} /> : <Menu size={24} />}
						</button>
						<div className="flex items-center">
							<User className="mr-2" size={24} />
							<h1 className="text-xl font-bold">Client Dashboard</h1>
						</div>
					</div>
					<div className="flex items-center">
						{user && (
							<div className="mr-4 hidden md:block">
								<span className="font-medium">{user.firstName} {user.lastName}</span>
							</div>
						)}
						<button
							onClick={() => navigate('/')}
							className="mr-3 flex items-center p-2 hover:bg-blue-700 rounded"
							title="Home"
						>
							<ChevronLeft size={20} />
							<span className="ml-1 hidden md:inline">Home</span>
						</button>
						<button
							onClick={handleLogout}
							className="flex items-center p-2 hover:bg-blue-700 rounded"
							title="Logout"
						>
							<LogOut size={20} />
							<span className="ml-1 hidden md:inline">Logout</span>
						</button>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<div className="flex-grow flex">
				{/* Sidebar - Desktop */}
				<aside className="w-64 bg-white shadow-md hidden lg:block">
					<div className="p-4 border-b">
						<h2 className="font-semibold text-gray-800">Navigation</h2>
					</div>
					<nav className="py-4">
						<ul>
							<li>
								<Link
									to="/client-profile"
									className={`flex items-center px-4 py-3 ${
										getCurrentSection() === 'profile'
											? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
											: 'text-gray-700 hover:bg-gray-100'
									}`}
								>
									<User size={20} className="mr-2" />
									<span>Dashboard</span>
								</Link>
							</li>
							<li>
								<Link
									to="/client-profile/responses"
									className={`flex items-center px-4 py-3 ${
										getCurrentSection() === 'responses'
											? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
											: 'text-gray-700 hover:bg-gray-100'
									}`}
								>
									<MessageSquare size={20} className="mr-2" />
									<span>Session Responses</span>
								</Link>
							</li>
							<li>
								<Link
									to="/client-profile/personality"
									className={`flex items-center px-4 py-3 ${
										getCurrentSection() === 'personality'
											? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
											: 'text-gray-700 hover:bg-gray-100'
									}`}
								>
									<BarChart size={20} className="mr-2" />
									<span>Personality Profile</span>
								</Link>
							</li>
						</ul>
					</nav>
				</aside>

				{/* Mobile Menu */}
				{menuOpen && (
					<div className="fixed inset-0 z-50 lg:hidden">
						<div 
							className="absolute inset-0 bg-gray-800 bg-opacity-50"
							onClick={() => setMenuOpen(false)}
						></div>
						<div className="absolute top-0 left-0 w-64 h-full bg-white shadow-lg">
							<div className="p-4 border-b flex justify-between items-center">
								<h2 className="font-semibold text-gray-800">Navigation</h2>
								<button 
									onClick={() => setMenuOpen(false)}
									className="p-1 hover:bg-gray-100 rounded-full"
								>
									<X size={20} />
								</button>
							</div>
							<nav className="py-4">
								<ul>
									<li>
										<Link
											to="/client-profile"
											className={`flex items-center px-4 py-3 ${
												getCurrentSection() === 'profile'
													? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
													: 'text-gray-700 hover:bg-gray-100'
											}`}
											onClick={() => setMenuOpen(false)}
										>
											<User size={20} className="mr-2" />
											<span>Dashboard</span>
										</Link>
									</li>
									<li>
										<Link
											to="/client-profile/responses"
											className={`flex items-center px-4 py-3 ${
												getCurrentSection() === 'responses'
													? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
													: 'text-gray-700 hover:bg-gray-100'
											}`}
											onClick={() => setMenuOpen(false)}
										>
											<MessageSquare size={20} className="mr-2" />
											<span>Session Responses</span>
										</Link>
									</li>
									<li>
										<Link
											to="/client-profile/personality"
											className={`flex items-center px-4 py-3 ${
												getCurrentSection() === 'personality'
													? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
													: 'text-gray-700 hover:bg-gray-100'
											}`}
											onClick={() => setMenuOpen(false)}
										>
											<BarChart size={20} className="mr-2" />
											<span>Personality Profile</span>
										</Link>
									</li>
								</ul>
							</nav>
						</div>
					</div>
				)}

				{/* Main Content Area */}
				<div className="flex-grow bg-blue-50 p-6">
					<div className="container mx-auto">
						{error ? (
							<div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
								{error}
								<button
									onClick={fetchClientProfile}
									className="ml-4 underline text-red-700"
								>
									Try Again
								</button>
							</div>
						) : (
							<>
								<div className="mb-6">
									<h2 className="text-2xl font-bold text-gray-800">{getPageTitle()}</h2>
									<p className="text-gray-600 mt-1">
										{getCurrentSection() === 'profile' 
											? 'Welcome to your personalized therapy dashboard'
											: getCurrentSection() === 'responses'
											? 'Review your responses from Natural Matchmaker sessions'
											: 'Explore your personality insights and compatibility'
										}
									</p>
								</div>

								{/* Start Session Button - Only show on main dashboard */}
								{getCurrentSection() === 'profile' && (
									<div className="mb-8">
										<div className="bg-white rounded-lg shadow-md p-6">
											<div className="text-center">
												<div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
													<Mic className="text-blue-600" size={32} />
												</div>
												<h3 className="text-xl font-semibold text-gray-800 mb-2">Find Your Perfect Therapist</h3>
												<p className="text-gray-600 mb-6">
													Use our AI-powered voice matching to discover the ideal therapist for your needs. 
													Have a natural conversation about your preferences and goals.
												</p>
												<button
													onClick={handleStartSession}
													className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
												>
													<Play size={20} className="mr-2" />
													Start a Session
												</button>
											</div>
										</div>
									</div>
								)}
								
								<div className="bg-white rounded-lg shadow-md p-6">
									{!profile && getCurrentSection() !== 'profile' ? (
										<div className="text-center py-8">
											<div className="rounded-full bg-yellow-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
												<Mic className="text-yellow-600" size={32} />
											</div>
											<h3 className="text-xl font-semibold text-gray-800 mb-2">No Sessions Yet</h3>
											<p className="text-gray-600 mb-6">
												You need to complete a Natural Matchmaker session first to view this section.
											</p>
											<button
												onClick={handleStartSession}
												className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
											>
												<Play size={16} className="mr-2" />
												Start Your First Session
											</button>
										</div>
									) : (
										<Routes>
											{/* Main profile route - show basic info */}
											<Route path="/" element={<ClientProfile profile={profile} user={user} refreshProfile={fetchClientProfile} />} />
											
											{/* Responses section - show session answers */}
											<Route path="/responses" element={<ClientResponses profile={profile} />} />
											
											{/* Personality section - show personality analysis */}
											<Route path="/personality" element={<ClientPersonality profile={profile} refreshProfile={fetchClientProfile} />} />
										</Routes>
									)}
								</div>
							</>
						)}
					</div>
				</div>
			</div>

			{/* Footer */}
			<footer className="bg-blue-800 text-blue-200 py-4 text-center text-sm">
				<div className="container mx-auto">
					Mindaera | Internal Dev Prototype | Not for public release
				</div>
			</footer>
		</div>
	);
};

export default ClientProfilePage;